// Corrido media references, as the API hands them to the page.
//
// Every still, clip and finished video comes back as an OPAQUE signed handle
// (utils/media-token.js: `m1.<base64url(path or URL)>.<hmac>`), never a raw
// path. /api/web/corrido/image is the only thing that turns a handle back into
// bytes. The gallery still needs to know whether a run's result is a VIDEO or
// a storyboard still (V2 storyboard rows carry the first PNG as result_url —
// listing those under "Finished videos" gave a broken player of a PNG with an
// .mp4 download). The base64 body is not a secret (it is already in the
// browser's hands); decoding it only to read the extension leaks nothing new.

const IMAGE_ROUTE = "/api/web/corrido/image";

/** The path/URL inside an `m1.` handle, or the input itself when it is not one. */
export function decodeMediaRef(ref: string | null | undefined): string {
  const s = String(ref || "");
  if (!s.startsWith("m1.")) return s;
  const parts = s.split(".");
  if (parts.length !== 3) return s;
  try {
    const b64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = b64 + "=".repeat((4 - (b64.length % 4)) % 4);
    return decodeURIComponent(
      Array.from(atob(padded), (c) => "%" + c.charCodeAt(0).toString(16).padStart(2, "0")).join(""),
    );
  } catch {
    return s;
  }
}

/** True when a media handle points at an .mp4 (query string and fragment ignored). */
export function mediaRefLooksLikeVideo(ref: string | null | undefined): boolean {
  const decoded = decodeMediaRef(ref);
  if (!decoded) return false;
  const clean = decoded.split("#")[0].split("?")[0];
  return /\.(mp4|m4v|webm|mov)$/i.test(clean);
}

/** A run row from GET /api/web/corrido/runs. `video`/`is_video` are the flags the
 *  server may add; the extension test covers rows that predate them. */
export function isFinishedVideoRun(run: {
  status?: string | null;
  result_url?: string | null;
  video?: boolean | null;
  is_video?: boolean | null;
}): boolean {
  if (!run || run.status !== "completed" || !run.result_url) return false;
  if (run.video === true || run.is_video === true) return true;
  // A cut is the only job whose result IS the video.
  if ((run as { type?: string }).type === "corrido_assemble") return true;
  return mediaRefLooksLikeVideo(run.result_url);
}

/** Auth-gated proxy URL for a media handle (the ONLY origin the page may use). */
export function corridoMediaUrl(ref: string, opts: { download?: boolean; version?: number } = {}): string {
  let u = `${IMAGE_ROUTE}?path=${encodeURIComponent(ref)}`;
  if (opts.version) u += `&v=${opts.version}`;
  if (opts.download) u += "&download=1";
  return u;
}
